import React, { useState, useRef, useEffect } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { resizeImage } from "../../utils/resizeImage";
import { 
    Container, Grid,
} from "@mui/material";
import { Article } from './Article';
import ImageModal from '../templates/ImageModal';
import ResizedImageCard from '../templates/ResizedImageCard';
import AppDescription from '../templates/AppDescription';
import UploadControls from '../templates/UploadControls';
import ModeSelector from '../templates/ModeSelector';
import ResizeButton from '../templates/ResizeButton';
import ZipDownloadButton from '../templates/ZipDownloadButton';
import SizeSelector from '../templates/SizeSelector';

const Tool = () => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [resizedImages, setResizedImages] = useState([]);
    const [currentSize, setCurrentSize] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [mode, setMode] = useState('aspect');  // ✅ 'aspect' or 'crop'
    const [cropPosition, setCropPosition] = useState('center');  // ✅ クロップ位置の初期値


    const fileInputRef = useRef(null);

    const availableSizes = [128, 256, 512, 1024, 2048];

    // ✅ アップロード画像情報（ファイル名とサイズ）
    const [fileInfo, setFileInfo] = useState([]);

    useEffect(() => {
        if (selectedFiles.length > 0) {
            const previews = selectedFiles.map((file) => URL.createObjectURL(file));
            const info = selectedFiles.map((file) => ({
                name: file.name,
                size: (file.size / 1024).toFixed(2) + " KB"
            }));

            setImagePreviews(previews);
            setFileInfo(info);
            setOpenModal(true);  // 自動でモーダル表示
        }
    }, [selectedFiles]);

    // ✅ ファイル選択処理
    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            setSelectedFiles(files);
            setResizedImages([]);
        }
    };

    // ファイル選択ダイアログを開く
    const triggerFileSelect = () => {
        fileInputRef.current?.click();
    };

    // ✅ 小数点以下を切り捨てたサイズ表示
    const formatDimensions = (width, height) => {
        return `${Math.floor(width)}x${Math.floor(height)}`;
    };

    // ✅ サイズと縮小率を計算（増減によって色分け）
    const calculateReduction = (originalSize, resizedSize) => {
        const originalKB = (originalSize / 1024).toFixed(2);
        const resizedKB = (resizedSize / 1024).toFixed(2);

        if (resizedSize <= originalSize) {
            // ✅ 削減の場合 → 緑色表示
            const reduction = ((1 - resizedSize / originalSize) * 100).toFixed(1);
            return {
                text: `${originalKB} KB → ${resizedKB} KB (${reduction}%削減)`,
                color: '#4CAF50'  // 緑色
            };
        } else {
            // ✅ 増加の場合 → 赤色表示
            const increase = ((resizedSize / originalSize - 1) * 100).toFixed(1);
            return {
                text: `${originalKB} KB → ${resizedKB} KB (+${increase}%増加)`,
                color: '#F44336'  // 赤色
            };
        }
    };


    // ✅ 画像をリサイズする関数
    const handleResize = async () => {
        console.log("✅ handleResize関数が呼び出されました！");

        if (selectedFiles.length === 0 || !currentSize) {
            console.warn("⚠️ ファイルまたはサイズがありません");
            return;
        }

        setIsResizing(true);

        try {
            const resized = await Promise.all(
                selectedFiles.map(async (file) => {
                    console.log(`処理中ファイル: ${file.name}`);

                    // ✅ `resizeImage`関数に任せる
                    const result = await resizeImage(
                        file,
                        currentSize,
                        currentSize,
                        mode,          // クロップ or 比率維持
                        cropPosition   // クロップ位置を渡す
                    );

                    console.log(`✅ リサイズ完了: ${result.url}`);

                    const blob = await (await fetch(result.url)).blob();

                    return {
                        url: result.url,
                        name: file.name,
                        width: result.width,
                        height: result.height,
                        originalSize: file.size,
                        resizedSize: blob.size
                    };
                })
            );

            console.log("🔥 リサイズ結果:", resized);
            setResizedImages(resized);
        } catch (error) {
            console.error('❌ リサイズ処理中にエラー:', error);
        } finally {
            setIsResizing(false);
        }
    };


    // ✅ ZIPダウンロード
    const downloadAllAsZip = async () => {
        if (resizedImages.length === 0) return;

        const zip = new JSZip();
        const folder = zip.folder('resized_images');

        await Promise.all(
            resizedImages.map(async ({ url, name, width, height }) => {
                const response = await fetch(url);
                const blob = await response.blob();
                folder.file(`${name}_${formatDimensions(width, height)}.png`, blob);
            })
        );

        zip.generateAsync({ type: 'blob' }).then(content => saveAs(content, 'resized_images.zip'));
    };

    // ✅ 個別ダウンロード関数
    const downloadSingleImage = async (image) => {
        const response = await fetch(image.url);
        const blob = await response.blob();
        const fileName = `${image.name}_${formatDimensions(image.width, image.height)}.png`;
        saveAs(blob, fileName);
    };

    const handleModeChange = (newMode) => {
        setMode(newMode);
    };

    const handleCropPositionChange = (position) => {
        setCropPosition(position);
    };

    

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* アプリ説明表示 */}
            <AppDescription />

            {/* アップロードボタンと画像一覧ボタン */}
            <UploadControls 
                fileInputRef={fileInputRef}
                handleFileChange={handleFileChange}
                triggerFileSelect={triggerFileSelect}
                selectedFiles={selectedFiles}
                setOpenModal={setOpenModal}
            />

            {/* モード選択 */}
            <ModeSelector
                mode={mode}
                handleModeChange={handleModeChange}
                cropPosition={cropPosition}
                handleCropPositionChange={handleCropPositionChange}
            />

            {/* サイズ選択 */}
            <SizeSelector
                currentSize={currentSize}
                setCurrentSize={setCurrentSize}
                availableSizes={availableSizes}
            />

            {/* リサイズボタン */}
            <ResizeButton
                handleResize={handleResize}
                isResizing={isResizing}
                selectedFiles={selectedFiles}
                currentSize={currentSize}
            />

            {/* ZIPダウンロードボタン */}
            <ZipDownloadButton 
                resizedImages={resizedImages}
                downloadAllAsZip={downloadAllAsZip}
            />


            {/* リサイズ後の画像と情報表示 */}
            <Grid container spacing={3}>
                {resizedImages.map((img, index) => (
                    <ResizedImageCard
                        key={index}
                        img={img}
                        calculateReduction={calculateReduction}
                        formatDimensions={formatDimensions}
                        downloadSingleImage={downloadSingleImage}
                    />
                ))}
            </Grid>

            {/* モーダル表示 */}
            <ImageModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                imagePreviews={imagePreviews}
                fileInfo={fileInfo}
            />

            {/* 記事表示 */}
            <Article />
        </Container>
    );
};

export default Tool;
